import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../hooks/useUrlParams';
import { carVariantAPI, BASE_URL } from '../services/api';
import './Brands.css';
import SearchableDropdown from '../components/SearchableDropdown';

const CarVariants = () => {
  const navigate = useNavigate();
  const [variants, setVariants] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination state
  const [page, setPage] = useUrlState('page', 1);
  const [limit, setLimit] = useUrlState('limit', 10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state
  const [search, setSearch] = useState('');
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');

  // Form modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [formBrandId, setFormBrandId] = useState('');
  const [formModels, setFormModels] = useState([]);
  const [formData, setFormData] = useState({
    modelId: '',
    variantName: '',
    price: '',
    exShowroomPrice: '',
    onRoadPrice: '',
    fuelType: '',
    transmissionType: '',
    engine: '',
    power: '',
    torque: '',
    mileage: '',
    seatingCapacity: '',
    bootSpace: '',
    fuelTankCapacity: '',
    groundClearance: '',
    topSpeed: '',
    driveType: '',
    ncapRating: '',
    length: '',
    width: '',
    height: '',
    wheelBase: '',
    kerbWeight: '',
    acceleration: ''
  });
  const [formLoading, setFormLoading] = useState(false);

  // Sync modal state
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncBrandSlug, setSyncBrandSlug] = useState('');
  const [syncModelSlug, setSyncModelSlug] = useState('');
  const [syncModels, setSyncModels] = useState([]);
  const [previewVariants, setPreviewVariants] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingVariants, setSyncingVariants] = useState({});
  const [syncError, setSyncError] = useState('');

  // Alert modal state
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success'); // 'success' or 'error'

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    fetchVariants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search, selectedModelId]);

  useEffect(() => {
    if (selectedBrandId) {
      fetchModels(selectedBrandId);
    } else {
      setModels([]);
      setSelectedModelId('');
    }
  }, [selectedBrandId]);

  const fetchBrands = async () => {
    try {
      const response = await carVariantAPI.getBrands();
      setBrands(response.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchModels = async (brandId) => {
    try {
      const response = await carVariantAPI.getModels({ brandId });
      setModels(response.data);
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  const fetchVariants = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        search: search || undefined,
        modelId: selectedModelId || undefined
      };
      const response = await carVariantAPI.getAll(params);
      setVariants(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
      setTotalPages(response.data.pagination?.totalPages || 0);
      setError('');
    } catch (error) {
      console.error('Error fetching variants:', error);
      setError('Failed to fetch car variants');
      setVariants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this variant?')) {
      try {
        await carVariantAPI.delete(id);
        fetchVariants();
      } catch (error) {
        console.error('Error deleting variant:', error);
        setAlertMessage('Failed to delete variant');
        setAlertType('error');
        setShowAlertModal(true);
      }
    }
  };

  const handleViewDetails = (id) => {
    navigate(`/car-variants/${id}`);
  };

  // Form modal handlers
  const handleCreate = async () => {
    setEditingVariant(null);
    // Use current filter selections if available
    setFormBrandId(selectedBrandId || '');
    if (selectedBrandId) {
      setFormModels(models);
    } else {
      setFormModels([]);
    }
    setFormData({
      modelId: selectedModelId || '',
      variantName: '',
      price: '',
      exShowroomPrice: '',
      onRoadPrice: '',
      fuelType: '',
      transmissionType: '',
      engine: '',
      power: '',
      torque: '',
      mileage: '',
      seatingCapacity: '',
      bootSpace: '',
      fuelTankCapacity: '',
      groundClearance: '',
      topSpeed: '',
      driveType: '',
      ncapRating: '',
      length: '',
      width: '',
      height: '',
      wheelBase: '',
      kerbWeight: '',
      acceleration: ''
    });
    setShowFormModal(true);
  };

  const handleEdit = async (variant) => {
    setEditingVariant(variant);
    // Get brand ID from the variant's model and fetch models for that brand
    const brandId = variant.model?.brand?.id || variant.model?.brandId || '';
    setFormBrandId(brandId);
    if (brandId) {
      try {
        const response = await carVariantAPI.getModels({ brandId });
        setFormModels(response.data);
      } catch (error) {
        console.error('Error fetching models for edit:', error);
        setFormModels([]);
      }
    } else {
      setFormModels([]);
    }
    setFormData({
      modelId: variant.modelId || '',
      variantName: variant.variantName || '',
      price: variant.price || '',
      exShowroomPrice: variant.exShowroomPrice || '',
      onRoadPrice: variant.onRoadPrice || '',
      fuelType: variant.fuelType || '',
      transmissionType: variant.transmissionType || '',
      engine: variant.engine || '',
      power: variant.power || '',
      torque: variant.torque || '',
      mileage: variant.mileage || '',
      seatingCapacity: variant.seatingCapacity || '',
      bootSpace: variant.bootSpace || '',
      fuelTankCapacity: variant.fuelTankCapacity || '',
      groundClearance: variant.groundClearance || '',
      topSpeed: variant.topSpeed || '',
      driveType: variant.driveType || '',
      ncapRating: variant.ncapRating || '',
      length: variant.length || '',
      width: variant.width || '',
      height: variant.height || '',
      wheelBase: variant.wheelBase || '',
      kerbWeight: variant.kerbWeight || '',
      acceleration: variant.acceleration || ''
    });
    setShowFormModal(true);
  };

  const handleFormBrandChange = async (brandId) => {
    setFormBrandId(brandId);
    setFormData(prev => ({ ...prev, modelId: '' }));
    if (brandId) {
      try {
        const response = await carVariantAPI.getModels({ brandId });
        setFormModels(response.data);
      } catch (error) {
        console.error('Error fetching models:', error);
        setFormModels([]);
      }
    } else {
      setFormModels([]);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.modelId || !formData.variantName) {
      setError('Model and Variant Name are required');
      return;
    }

    setFormLoading(true);
    try {
      if (editingVariant) {
        await carVariantAPI.update(editingVariant.id, formData);
      } else {
        await carVariantAPI.create(formData);
      }
      setShowFormModal(false);
      setEditingVariant(null);
      fetchVariants();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save car variant');
    } finally {
      setFormLoading(false);
    }
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingVariant(null);
  };

  // Sync modal functions
  const openSyncModal = () => {
    setShowSyncModal(true);
    setSyncBrandSlug('');
    setSyncModelSlug('');
    setSyncModels([]);
    setPreviewVariants([]);
    setSyncError('');
  };

  const closeSyncModal = () => {
    setShowSyncModal(false);
    setPreviewVariants([]);
    setSyncError('');
    setSyncBrandSlug('');
    setSyncModelSlug('');
  };

  const handleSyncBrandChange = async (brandSlug) => {
    setSyncBrandSlug(brandSlug);
    setSyncModelSlug('');
    setPreviewVariants([]);

    if (brandSlug) {
      const brand = brands.find(b => b.slug === brandSlug);
      if (brand) {
        try {
          const response = await carVariantAPI.getModels({ brandId: brand.id });
          setSyncModels(response.data);
        } catch (error) {
          console.error('Error fetching models for sync:', error);
        }
      }
    } else {
      setSyncModels([]);
    }
  };

  const handlePreviewVariants = async () => {
    if (!syncBrandSlug || !syncModelSlug) {
      setSyncError('Please select both brand and model');
      return;
    }

    try {
      setPreviewLoading(true);
      setSyncError('');
      const response = await carVariantAPI.preview({ brandSlug: syncBrandSlug, modelSlug: syncModelSlug });
      setPreviewVariants(response.data.variants || []);
    } catch (error) {
      console.error('Error previewing variants:', error);
      setSyncError('Failed to fetch variants from CardDekho: ' + (error.response?.data?.error || error.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSyncSingle = async (variant) => {
    try {
      setSyncingVariants(prev => ({ ...prev, [variant.slug]: true }));
      await carVariantAPI.syncSingle(variant);
      setPreviewVariants(prev => prev.map(v =>
        v.slug === variant.slug ? { ...v, existsInDb: true } : v
      ));
      fetchVariants();
    } catch (error) {
      console.error('Error syncing variant:', error);
      setSyncError('Failed to sync variant: ' + (error.response?.data?.error || error.message));
    } finally {
      setSyncingVariants(prev => ({ ...prev, [variant.slug]: false }));
    }
  };

  const handleSyncAll = async () => {
    if (!syncBrandSlug || !syncModelSlug) {
      setSyncError('Please select both brand and model');
      return;
    }

    try {
      setSyncingAll(true);
      await carVariantAPI.sync({ brandSlug: syncBrandSlug, modelSlug: syncModelSlug });
      await handlePreviewVariants();
      fetchVariants();
      setAlertMessage('All variants synced successfully!');
      setAlertType('success');
      setShowAlertModal(true);
    } catch (error) {
      console.error('Error syncing all variants:', error);
      setSyncError('Failed to sync variants: ' + (error.response?.data?.error || error.message));
    } finally {
      setSyncingAll(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    if (typeof price === 'string') {
      // Check if already formatted with currency indicators (including abbreviations)
      if (price.includes('Lakh') || price.includes('Crore') || price.includes('Rs') ||
          price.includes('Cr') || price.includes('L') || price.includes('₹')) {
        return price;
      }
      const numPrice = parseFloat(price.replace(/[^0-9.]/g, ''));
      if (!isNaN(numPrice) && numPrice > 0) {
        if (numPrice >= 10000000) {
          return `Rs. ${(numPrice / 10000000).toFixed(2)} Crore`;
        } else if (numPrice >= 100000) {
          return `Rs. ${(numPrice / 100000).toFixed(2)} Lakh`;
        }
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(numPrice);
      }
      return price;
    }
    if (typeof price === 'number') {
      if (price >= 10000000) {
        return `Rs. ${(price / 10000000).toFixed(2)} Crore`;
      } else if (price >= 100000) {
        return `Rs. ${(price / 100000).toFixed(2)} Lakh`;
      }
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);
    }
    return '-';
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${BASE_URL}${imagePath}`;
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleBrandFilterChange = (value) => {
    setSelectedBrandId(value);
    setPage(1);
  };

  const handleModelFilterChange = (value) => {
    setSelectedModelId(value);
    setPage(1);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${page === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <div className="pagination-info">
          Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} entries
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(1)}
            disabled={page === 1}
          >
            First
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
          >
            Prev
          </button>
          {pages}
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
          >
            Next
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(totalPages)}
            disabled={page === totalPages}
          >
            Last
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="brands-page">
      {/* Alert Modal */}
      {showAlertModal && (
        <div className="modal-overlay alert-modal-overlay" onClick={() => setShowAlertModal(false)}>
          <div className="alert-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`alert-modal-icon ${alertType}`}>
              {alertType === 'success' ? (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              ) : (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              )}
            </div>
            <p className="alert-modal-message">{alertMessage}</p>
            <button
              className={`btn ${alertType === 'success' ? 'btn-primary' : 'btn-danger'}`}
              onClick={() => setShowAlertModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {showFormModal && (
        <div className="modal-overlay" onClick={closeFormModal}>
          <div className="form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingVariant ? 'Edit Car Variant' : 'Add Car Variant'}</h2>
              <button className="modal-close" onClick={closeFormModal}>&times;</button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Brand *</label>
                    <select
                      value={formBrandId}
                      onChange={(e) => handleFormBrandChange(e.target.value)}
                      required
                    >
                      <option value="">Select Brand</option>
                      {brands.map(brand => (
                        <option key={brand.id} value={brand.id}>{brand.brandName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Model *</label>
                    <select
                      name="modelId"
                      value={formData.modelId}
                      onChange={handleFormChange}
                      required
                      disabled={!formBrandId}
                    >
                      <option value="">Select Model</option>
                      {formModels.map(model => (
                        <option key={model.id} value={model.id}>{model.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Variant Name *</label>
                  <input
                    type="text"
                    name="variantName"
                    value={formData.variantName}
                    onChange={handleFormChange}
                    placeholder="e.g., LXi, VXi, ZXi+"
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Price</label>
                    <input
                      type="text"
                      name="price"
                      value={formData.price}
                      onChange={handleFormChange}
                      placeholder="e.g., Rs. 8.5 Lakh"
                    />
                  </div>
                  <div className="form-group">
                    <label>Ex-Showroom Price</label>
                    <input
                      type="text"
                      name="exShowroomPrice"
                      value={formData.exShowroomPrice}
                      onChange={handleFormChange}
                      placeholder="e.g., Rs. 7.9 Lakh"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>On-Road Price</label>
                  <input
                    type="text"
                    name="onRoadPrice"
                    value={formData.onRoadPrice}
                    onChange={handleFormChange}
                    placeholder="e.g., Rs. 9.5 Lakh"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Fuel Type</label>
                    <select
                      name="fuelType"
                      value={formData.fuelType}
                      onChange={handleFormChange}
                    >
                      <option value="">Select Fuel Type</option>
                      <option value="Petrol">Petrol</option>
                      <option value="Diesel">Diesel</option>
                      <option value="Electric">Electric</option>
                      <option value="Hybrid">Hybrid</option>
                      <option value="CNG">CNG</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Transmission</label>
                    <select
                      name="transmissionType"
                      value={formData.transmissionType}
                      onChange={handleFormChange}
                    >
                      <option value="">Select Transmission</option>
                      <option value="Manual">Manual</option>
                      <option value="Automatic">Automatic</option>
                      <option value="AMT">AMT</option>
                      <option value="CVT">CVT</option>
                      <option value="DCT">DCT</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Engine</label>
                    <input
                      type="text"
                      name="engine"
                      value={formData.engine}
                      onChange={handleFormChange}
                      placeholder="e.g., 1197 cc"
                    />
                  </div>
                  <div className="form-group">
                    <label>Mileage</label>
                    <input
                      type="text"
                      name="mileage"
                      value={formData.mileage}
                      onChange={handleFormChange}
                      placeholder="e.g., 22.05 kmpl"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Power</label>
                    <input
                      type="text"
                      name="power"
                      value={formData.power}
                      onChange={handleFormChange}
                      placeholder="e.g., 88.5 bhp"
                    />
                  </div>
                  <div className="form-group">
                    <label>Torque</label>
                    <input
                      type="text"
                      name="torque"
                      value={formData.torque}
                      onChange={handleFormChange}
                      placeholder="e.g., 113 Nm"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Seating Capacity</label>
                    <input
                      type="text"
                      name="seatingCapacity"
                      value={formData.seatingCapacity}
                      onChange={handleFormChange}
                      placeholder="e.g., 5"
                    />
                  </div>
                  <div className="form-group">
                    <label>Boot Space</label>
                    <input
                      type="text"
                      name="bootSpace"
                      value={formData.bootSpace}
                      onChange={handleFormChange}
                      placeholder="e.g., 328 litres"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Fuel Tank Capacity</label>
                    <input
                      type="text"
                      name="fuelTankCapacity"
                      value={formData.fuelTankCapacity}
                      onChange={handleFormChange}
                      placeholder="e.g., 37 litres"
                    />
                  </div>
                  <div className="form-group">
                    <label>Ground Clearance</label>
                    <input
                      type="text"
                      name="groundClearance"
                      value={formData.groundClearance}
                      onChange={handleFormChange}
                      placeholder="e.g., 170 mm"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Top Speed</label>
                    <input
                      type="text"
                      name="topSpeed"
                      value={formData.topSpeed}
                      onChange={handleFormChange}
                      placeholder="e.g., 180 kmph"
                    />
                  </div>
                  <div className="form-group">
                    <label>Acceleration (0-100)</label>
                    <input
                      type="text"
                      name="acceleration"
                      value={formData.acceleration}
                      onChange={handleFormChange}
                      placeholder="e.g., 11.5 seconds"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Drive Type</label>
                    <select
                      name="driveType"
                      value={formData.driveType}
                      onChange={handleFormChange}
                    >
                      <option value="">Select Drive Type</option>
                      <option value="FWD">FWD (Front Wheel Drive)</option>
                      <option value="RWD">RWD (Rear Wheel Drive)</option>
                      <option value="AWD">AWD (All Wheel Drive)</option>
                      <option value="4WD">4WD (4 Wheel Drive)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>NCAP Rating</label>
                    <input
                      type="text"
                      name="ncapRating"
                      value={formData.ncapRating}
                      onChange={handleFormChange}
                      placeholder="e.g., 5 Star"
                    />
                  </div>
                </div>
                <h4 style={{ marginTop: '16px', marginBottom: '12px', color: '#333' }}>Dimensions</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Length</label>
                    <input
                      type="text"
                      name="length"
                      value={formData.length}
                      onChange={handleFormChange}
                      placeholder="e.g., 3995 mm"
                    />
                  </div>
                  <div className="form-group">
                    <label>Width</label>
                    <input
                      type="text"
                      name="width"
                      value={formData.width}
                      onChange={handleFormChange}
                      placeholder="e.g., 1735 mm"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Height</label>
                    <input
                      type="text"
                      name="height"
                      value={formData.height}
                      onChange={handleFormChange}
                      placeholder="e.g., 1515 mm"
                    />
                  </div>
                  <div className="form-group">
                    <label>Wheel Base</label>
                    <input
                      type="text"
                      name="wheelBase"
                      value={formData.wheelBase}
                      onChange={handleFormChange}
                      placeholder="e.g., 2500 mm"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Kerb Weight</label>
                  <input
                    type="text"
                    name="kerbWeight"
                    value={formData.kerbWeight}
                    onChange={handleFormChange}
                    placeholder="e.g., 1050 kg"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeFormModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (editingVariant ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sync Modal */}
      {showSyncModal && (
        <div className="modal-overlay">
          <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Sync Car Variants</h2>
              <button className="modal-close" onClick={closeSyncModal}>&times;</button>
            </div>
            <div className="modal-body sync-modal-body">
              <div className="sync-brand-selector">
                <label>Select Brand and Model to Sync:</label>
                <div className="sync-brand-row">
                  <select
                    value={syncBrandSlug}
                    onChange={(e) => handleSyncBrandChange(e.target.value)}
                    className="brand-select"
                  >
                    <option value="">-- Select Brand --</option>
                    {brands.map(brand => (
                      <option key={brand.id} value={brand.slug}>{brand.brandName}</option>
                    ))}
                  </select>
                  <select
                    value={syncModelSlug}
                    onChange={(e) => setSyncModelSlug(e.target.value)}
                    className="brand-select"
                    disabled={!syncBrandSlug}
                  >
                    <option value="">-- Select Model --</option>
                    {syncModels.map(model => (
                      <option key={model.id} value={model.slug}>{model.name}</option>
                    ))}
                  </select>
                  <button
                    className="btn btn-primary"
                    onClick={handlePreviewVariants}
                    disabled={!syncBrandSlug || !syncModelSlug || previewLoading}
                  >
                    {previewLoading ? 'Loading...' : 'Load Variants'}
                  </button>
                </div>
              </div>

              {syncError && (
                <div className="sync-error">
                  <div className="error-icon">&#10005;</div>
                  <p>{syncError}</p>
                </div>
              )}

              {previewLoading && (
                <div className="sync-progress">
                  <div className="spinner"></div>
                  <p>Loading variants from CardDekho...</p>
                </div>
              )}

              {!previewLoading && previewVariants.length > 0 && (
                <div className="preview-section">
                  <div className="preview-brand-header">
                    <h3>{syncModels.find(m => m.slug === syncModelSlug)?.name} Variants</h3>
                    <span className="model-count">{previewVariants.length} variants found</span>
                  </div>
                  <div className="preview-variants-list">
                    {previewVariants.map((variant, index) => (
                      <div key={index} className="preview-variant-item">
                        <div className="preview-variant-main">
                          <div className="preview-variant-name">
                            <h4>{variant.variantName}</h4>
                            {variant.existsInDb && <span className="synced-badge">Synced</span>}
                          </div>
                          <div className="preview-variant-price">
                            {variant.price || variant.exShowroomPrice || 'Price N/A'}
                          </div>
                        </div>
                        <div className="preview-variant-specs">
                          {variant.fuelType && <span className="mini-spec">{variant.fuelType}</span>}
                          {variant.transmissionType && <span className="mini-spec">{variant.transmissionType}</span>}
                          {variant.engine && <span className="mini-spec">{variant.engine}</span>}
                        </div>
                        <div className="preview-variant-action">
                          <button
                            className={`btn btn-sm ${variant.existsInDb ? 'btn-update' : 'btn-add'}`}
                            onClick={() => handleSyncSingle(variant)}
                            disabled={syncingVariants[variant.slug]}
                          >
                            {syncingVariants[variant.slug]
                              ? '...'
                              : variant.existsInDb ? 'Update' : 'Add'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeSyncModal}>
                Close
              </button>
              <button
                className="btn btn-sync"
                onClick={handleSyncAll}
                disabled={syncingAll || previewLoading || !syncBrandSlug || !syncModelSlug || previewVariants.length === 0}
              >
                {syncingAll ? 'Syncing All...' : 'Sync All Variants'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <h1>Car Variants</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={handleCreate}>
            + Add Variant
          </button>
          <button className="btn btn-sync" onClick={openSyncModal}>
            Sync Data
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="table-controls">
          <div className="table-filters">
            <input
              type="text"
              placeholder="Search by variant name..."
              value={search}
              onChange={handleSearchChange}
              className="search-input"
            />
            <SearchableDropdown
              options={brands.map(brand => ({ value: String(brand.id), label: brand.brandName }))}
              value={String(selectedBrandId || '')}
              onChange={handleBrandFilterChange}
              placeholder="Search brands..."
              allLabel="All Brands"
            />
            <SearchableDropdown
              options={models.map(model => ({ value: String(model.id), label: model.name }))}
              value={String(selectedModelId || '')}
              onChange={handleModelFilterChange}
              placeholder="Search models..."
              allLabel="All Models"
              disabled={!selectedBrandId}
            />
          </div>
          <div className="table-options">
            <label>
              Show
              <select value={limit} onChange={handleLimitChange}>
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
              entries
            </label>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : variants.length === 0 ? (
          <div className="empty-state">
            <p>No car variants found. {search || selectedBrandId || selectedModelId ? 'Try adjusting your filters.' : 'Sync variants from CardDekho!'}</p>
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Image</th>
                  <th>Brand</th>
                  <th>Model</th>
                  <th>Variant Name</th>
                  <th>Price</th>
                  <th>Fuel Type</th>
                  <th>Transmission</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {variants.map((variant) => (
                  <tr key={variant.id}>
                    <td>{variant.id}</td>
                    <td>
                      <div className="brand-logo model-image">
                        {variant.image ? (
                          <img
                            src={getImageUrl(variant.image)}
                            alt={variant.variantName}
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <div className="logo-placeholder" style={{ display: variant.image ? 'none' : 'flex' }}>
                          {variant.variantName?.charAt(0) || 'V'}
                        </div>
                      </div>
                    </td>
                    <td>{variant.model?.brand?.brandName || '-'}</td>
                    <td>{variant.model?.name || '-'}</td>
                    <td>
                      {variant.variantName}
                      {/* Retired upstream: absent from the sync preview and can
                          never gain specs, so flag it here — this table is the
                          only place an admin can see these rows at all. */}
                      {variant.variantStatus === 'DISCONTINUED' && (
                        <span
                          title="No longer listed by the data source — specifications cannot be synced"
                          style={{
                            marginLeft: 8, padding: '2px 8px', borderRadius: 10,
                            fontSize: 11, fontWeight: 600,
                            background: '#fef3c7', color: '#92400e', whiteSpace: 'nowrap',
                          }}
                        >
                          Discontinued
                        </span>
                      )}
                    </td>
                    <td>{formatPrice(variant.exShowroomPrice || variant.price)}</td>
                    <td>{variant.fuelType || '-'}</td>
                    <td>{variant.transmissionType || '-'}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-view"
                          onClick={() => handleViewDetails(variant.id)}
                        >
                          View
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEdit(variant)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(variant.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && renderPagination()}
          </>
        )}
      </div>

      <style jsx>{`
        .form-modal {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }
        .form-modal .modal-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 20px 24px;
          border-radius: 12px 12px 0 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: relative;
        }
        .form-modal .modal-header h2 {
          margin: 0;
          font-size: 1.3rem;
          font-weight: 600;
        }
        .form-modal .modal-close {
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          font-size: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }
        .form-modal .modal-close:hover {
          background: rgba(255,255,255,0.3);
        }
        .form-modal .modal-body {
          padding: 24px;
        }
        .form-modal .modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #eee;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }
        .form-group {
          margin-bottom: 16px;
          text-align: left;
        }
        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: #333;
          font-size: 14px;
          text-align: left;
        }
        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .header-actions {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .btn-secondary {
          background: #f0f0f0;
          color: #333;
          border: 1px solid #ddd;
        }
        .btn-secondary:hover {
          background: #e0e0e0;
        }
        .alert-modal-overlay {
          z-index: 10000;
        }
        .alert-modal {
          background: white;
          border-radius: 16px;
          padding: 32px;
          text-align: center;
          max-width: 400px;
          width: 90%;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
          animation: modalSlideIn 0.3s ease;
          z-index: 10001;
        }
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .alert-modal-icon {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
        }
        .alert-modal-icon.success {
          background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
          color: #28a745;
        }
        .alert-modal-icon.error {
          background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
          color: #dc3545;
        }
        .alert-modal-message {
          font-size: 18px;
          color: #333;
          margin: 0 0 24px 0;
          font-weight: 500;
        }
        .alert-modal .btn {
          min-width: 120px;
          padding: 12px 32px;
          font-size: 15px;
          border-radius: 8px;
        }
      `}</style>
    </div>
  );
};

export default CarVariants;
